import React, { useState, useEffect } from "react";
import { 
    Card, Table, Row, Col, Badge, Spinner, Alert, Button,
    InputGroup, Form, ProgressBar, OverlayTrigger, Tooltip, Modal, Pagination
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";

export default function InventaireEnCours() {
    const navigate = useNavigate();
    const [inventaires, setInventaires] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        fetchInventaires();
    }, []);

    const fetchInventaires = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/inventaires.php?statut=brouillon`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setInventaires(result.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getStatutBadge = (statut) => {
        return (
            <span className="status-badge" style={{ background: '#f59e0b15', color: '#f59e0b' }}>
                <i className="ri-time-line me-1 fs-10"></i>
                Brouillon
            </span>
        );
    };

    const filteredInventaires = inventaires.filter(i => 
        i.reference_inventaire?.toLowerCase().includes(filterText.toLowerCase())
    );

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredInventaires.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredInventaires.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                    <span className="visually-hidden">Chargement...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="danger" className="rounded-3">
                <div className="d-flex align-items-center">
                    <i className="ri-error-warning-line fs-4 me-3 text-danger"></i>
                    <div>
                        <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                        <p className="mb-0 small">{error}</p>
                        <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchInventaires}>
                            <i className="ri-refresh-line me-1"></i>Réessayer
                        </Button>
                    </div>
                </div>
            </Alert>
        );
    }

    return (
        <>
            {/* Filtres modernes */}
            <Card className="border-0 shadow-sm rounded-3 mb-4">
                <Card.Body className="py-3 px-4">
                    <Row className="align-items-center g-3">
                        <Col md={8}>
                            <div className="position-relative">
                                <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                                <Form.Control
                                    type="text"
                                    placeholder="Rechercher un inventaire..."
                                    value={filterText}
                                    onChange={(e) => setFilterText(e.target.value)}
                                    className="ps-5 rounded-pill"
                                />
                            </div>
                        </Col>
                        <Col md={4} className="text-end">
                            <span className="badge bg-light text-dark rounded-pill px-3 py-2">
                                <i className="ri-stack-line me-1"></i>
                                {filteredInventaires.length} inventaire(s)
                            </span>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Liste avec pagination */}
            <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <Table hover className="align-middle mb-0 custom-table">
                            <thead className="bg-light">
                                <tr>
                                    <th className="ps-4">Référence</th>
                                    <th>Date</th>
                                    <th>Créé par</th>
                                    <th className="text-center">Produits</th>
                                    <th className="text-center">Progression</th>
                                    <th>Statut</th>
                                    <th className="text-center pe-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems.length > 0 ? (
                                    currentItems.map((inv, index) => (
                                        <tr key={inv.id_inventaire} style={{
                                            animation: `fadeIn 0.3s ease ${index * 0.05}s both`
                                        }}>
                                            <td className="ps-4">
                                                <Link to={`/stock/inventaire/${inv.id_inventaire}`} className="table-link">
                                                    {inv.reference_inventaire}
                                                </Link>
                                            </td>
                                            <td>{formatDate(inv.date_inventaire)}</td>
                                            <td>{inv.prenom_utilisateur} {inv.nom_utilisateur}</td>
                                            <td className="text-center">
                                                <Badge className="rounded-pill px-3" style={{ background: '#e2f3f5', color: '#fff' }}>
                                                    {inv.nb_produits || 0}
                                                </Badge>
                                           </td>
                                            <td className="text-center" style={{ minWidth: '120px' }}>
                                                <div className="progress-bar-custom" style={{ width: '100px', margin: '0 auto' }}>
                                                    <div className="progress-fill" style={{ width: `${Math.min((inv.nb_produits_saisis / inv.nb_produits) * 100, 100) || 0}%`, background: '#f59e0b' }}></div>
                                                </div>
                                            </td>
                                            <td>{getStatutBadge(inv.statut)}</td>
                                            <td className="text-center pe-4">
                                                <div className="d-flex gap-2 justify-content-center">
                                                    <OverlayTrigger placement="top" overlay={<Tooltip>Continuer l'inventaire</Tooltip>}>
                                                        <Button 
                                                            variant="outline-primary" 
                                                            size="sm"
                                                            className="action-btn"
                                                            onClick={() => navigate(`/stock/inventaire/${inv.id_inventaire}`)}
                                                        >
                                                            <i className="ri-play-line"></i>
                                                        </Button>
                                                    </OverlayTrigger>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center py-5">
                                            <div className="empty-state">
                                                <i className="ri-inbox-line empty-icon text-primary" style={{ fontSize: '4rem' }}></i>
                                                <h5 className="mt-3">Aucun inventaire en cours</h5>
                                                <p className="text-muted mb-3">Commencez un nouvel inventaire</p>
                                                {/* <Button 
                                                    variant="primary" 
                                                    onClick={() => navigate('/stock/inventaire/nouveau')}
                                                    className="rounded-pill"
                                                    style={{ background: '#21ACCD', border: 'none' }}
                                                >
                                                    <i className="ri-add-line me-2"></i>Nouvel inventaire
                                                </Button> */}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center p-3 border-top">
                            <div className="text-muted small">
                                <i className="ri-file-list-line me-1"></i>
                                Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredInventaires.length)} sur {filteredInventaires.length} inventaires
                            </div>
                            <Pagination className="mb-0">
                                <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                {renderPagination()}
                                <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                            </Pagination>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                /* Table Link */
                .table-link {
                    color: #21ACCD;
                    text-decoration: none;
                    font-weight: 600;
                }
                .table-link:hover {
                    text-decoration: underline;
                }
                
                /* Status Badge */
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.3rem 0.8rem;
                    border-radius: 30px;
                    font-size: 0.7rem;
                    font-weight: 500;
                }
                
                /* Action Buttons */
                .action-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }
                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .btn-outline-primary:hover {
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                    border-color: #21ACCD;
                    color: white;
                }
                
                /* Progress Bar */
                .progress-bar-custom {
                    background: #e2e8f0;
                    border-radius: 4px;
                    height: 6px;
                    overflow: hidden;
                }
                .progress-fill {
                    height: 100%;
                    border-radius: 4px;
                    transition: width 0.3s ease;
                }
                
                /* Custom Table */
                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .custom-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                /* Pagination */
                .pagination {
                    gap: 5px;
                }
                .pagination .page-item .page-link {
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 1rem;
                    transition: all 0.2s ease;
                    font-weight: 500;
                }
                .pagination .page-item.active .page-link {
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                    border-color: #21ACCD;
                    color: white;
                    box-shadow: 0 4px 10px rgba(33, 172, 205, 0.3);
                }
                .pagination .page-item .page-link:hover {
                    background: #f1f5f9;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .pagination .page-item.disabled .page-link {
                    opacity: 0.5;
                    pointer-events: none;
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-10 {
                    font-size: 0.625rem;
                }
            `}</style>
        </>
    );
}